import { apiClient } from '@leap4/frontend';
import { trackedLinesBaseUrl, trackedPublishersBaseUrl } from '../../../config/generals';
import {
    IActivateLineResult,
    ICheckResult,
    ISweepLog,
    ISweepStatus,
    IComposeLinesResult,
    IDistinctLineSpec,
    IOldLineRow,
    IImportResult,
    IImportToAllResult,
    IOnboardingImportResult,
    IOnboardingPreview,
    OnboardingResolutions,
    ITrackedLine,
    ITrackedPublisher,
    IUpdateList,
} from './appAdsLines.interface';

/**
 * One place for every Publisher Lines request, so components don't spread URL strings around.
 * Reads go through the collection-level `/actions/...` endpoints where the backend needs to
 * aggregate; plain CRUD uses the inherited routes.
 */

// ============================= publishers =============================

export const fetchPublishersWithCounts = async (): Promise<ITrackedPublisher[]> => {
    const res = await apiClient.get(`${trackedPublishersBaseUrl}/actions/withCounts`);
    return res.data as ITrackedPublisher[];
};

export const fetchPublisher = async (id: string): Promise<ITrackedPublisher> => {
    const res = await apiClient.get(`${trackedPublishersBaseUrl}/${id}`);
    return res.data as ITrackedPublisher;
};

/** Resolves the domain carried in the URL to a publisher. */
export const createPublisher = async (publisher: Partial<ITrackedPublisher>) => {
    const res = await apiClient.post(trackedPublishersBaseUrl, publisher);
    return res.data as ITrackedPublisher;
};

export const updatePublisher = async (id: string, publisher: Partial<ITrackedPublisher>) => {
    const res = await apiClient.patch(`${trackedPublishersBaseUrl}/${id}`, publisher);
    return res.data as ITrackedPublisher;
};

/**
 * Parks a publisher, or brings it back.
 *
 * Inactive is not deleted: the publisher keeps its lines and history, stays on this list, and
 * is simply left out of the operations that act on every publisher - sweeps, combined update
 * lists, bulk line pushes - and out of the seat-wide line views.
 */
export const setPublisherActive = async (id: string, isActive: boolean) => {
    const res = await apiClient.patch(`${trackedPublishersBaseUrl}/${id}/active`, { isActive });
    return res.data;
};

export const archivePublisher = async (id: string) => {
    await apiClient.post(`${trackedPublishersBaseUrl}/${id}/archive`, { confirmed: true });
};

export const fetchArchivedPublishers = async (): Promise<ITrackedPublisher[]> => {
    const res = await apiClient.get(`${trackedPublishersBaseUrl}/actions/archived`);
    return res.data;
};

export const restorePublisher = async (id: string) => {
    await apiClient.post(`${trackedPublishersBaseUrl}/${id}/restore`, {});
};

export const deleteArchivedPublishers = async (publisherIds: string[]): Promise<{ deleted: number; skipped: number }> => {
    const res = await apiClient.post(`${trackedPublishersBaseUrl}/actions/archived/deleteSelected`, {
        publisherIds, confirmed: true,
    });
    return res.data;
};

// ============================= chunking =============================

/**
 * Rows per request.
 *
 * main.ts caps request bodies at 1MB (`bodyParser.json({ limit: '1mb' })`), and a few thousand
 * app-ads.txt lines comfortably exceeds that - a large import would 413 with no useful error.
 * Batching keeps every request well inside the limit rather than raising a global setting for
 * one feature's benefit.
 *
 * Batches are sent SEQUENTIALLY, not in parallel, and that matters: each import call dedupes
 * against the lines already stored, so a duplicate spanning two batches is caught only if the
 * first batch has landed before the second is checked.
 */
export const IMPORT_CHUNK_SIZE = 400;

function chunk<T>(items: T[], size: number): T[][] {
    const out: T[][] = [];
    for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
    return out;
}

/** Runs `send` over sequential batches, folding results with `merge`. */
async function inChunks<TItem, TResult>(
    items: TItem[],
    send: (batch: TItem[]) => Promise<TResult>,
    merge: (a: TResult, b: TResult) => TResult,
    empty: TResult,
): Promise<TResult> {
    const batches = chunk(items, IMPORT_CHUNK_SIZE);
    if (!batches.length) return empty;

    let acc: TResult | null = null;
    for (const batch of batches) {
        const result = await send(batch);
        acc = acc === null ? result : merge(acc, result);
    }
    return acc as TResult;
}

const mergeImport = (a: IImportResult, b: IImportResult): IImportResult => ({
    added: a.added + b.added,
    skipped: a.skipped + b.skipped,
    invalid: (a.invalid ?? 0) + (b.invalid ?? 0),
    rowsRead: (a.rowsRead ?? 0) + (b.rowsRead ?? 0),
});

const EMPTY_IMPORT: IImportResult = { added: 0, skipped: 0, invalid: 0, rowsRead: 0 };

/** The CSV shape the bulk-add flow expects, offered as a download so nobody has to guess. */
export const downloadLinesTemplate = () => {
    // Each example is one whole line, and it contains commas - so it has to be quoted, or a
    // spreadsheet splits it across four columns and the single "Line" header stops matching
    // the data underneath it.
    const rows = [
        'Line',
        '"pubmatic.com, 161599, RESELLER, 5d62403b186f2ace"',
        '"conversantmedia.com, 100353, RESELLER"',
    ];
    const csv = rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', 'app-ads-lines-template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(link.href), 2000);
};

// ============================= onboarding bulk upload =============================

/**
 * Dry run of the onboarding sheet: what would be created, what already exists, and every row
 * problem found.
 *
 * Not chunked, unlike the line importers. At the couple of hundred rows this is meant for the
 * whole file sits well inside the 1MB body cap, and both duplicate detection and an
 * all-or-nothing decision need the entire file in one place - chunking would buy nothing and
 * would make a half-applied import possible.
 */
export const previewOnboardingSheet = async (
    rows: string[][],
    resolutions?: OnboardingResolutions,
): Promise<IOnboardingPreview> => {
    const res = await apiClient.post(`${trackedPublishersBaseUrl}/actions/onboarding/preview`, {
        rows,
        resolutions,
    });
    return res.data as IOnboardingPreview;
};

/** Commits the sheet. Additive only: creates publishers and lines, removes nothing. */
export const importOnboardingSheet = async (
    rows: string[][],
    resolutions?: OnboardingResolutions,
): Promise<IOnboardingImportResult> => {
    const res = await apiClient.post(`${trackedPublishersBaseUrl}/actions/onboarding/import`, {
        rows,
        resolutions,
    });
    return res.data as IOnboardingImportResult;
};

/**
 * The 7-column shape, offered as a download so nobody reverse-engineers it from an error.
 *
 * The last column is headed "Partner" rather than "Demand" to match the sheet ad-ops already
 * keep by hand - it's the demand partner either way, and their spelling is the one they'll
 * recognise. Nothing depends on the text: the parser skips the header row and reads columns by
 * position, so a file headed either way imports identically.
 */
export const downloadOnboardingTemplate = () => {
    // The Line column holds a whole ads.txt record and therefore contains commas, so it has to
    // be quoted or a spreadsheet splits it across four columns.
    const rows = [
        'Publisher Name,Domain,APP-ads.txt,Seller ID,Account manager,Line,Partner',
        'Wurl,wurl.com,wurl.com/app-ads.txt,1035,Nadav Asher,"themediasense.com, 1035, DIRECT",TMS',
        'Wurl,wurl.com,wurl.com/app-ads.txt,1035,Nadav Asher,"lijit.com, 562448, RESELLER, fafdf38b16bf6b2b",Sovrn',
        'OrkaTV,orka.tv,orka.tv/app-ads.txt,1071,"Nadav Asher, Idan","adte.com, 1071, DIRECT",',
    ];
    saveCsvText(rows.join('\n'), 'publisher-onboarding-template.csv');
};

/** Saves the issue list so a 400-row file can be fixed in one pass, not twelve at a time. */
export const downloadOnboardingIssues = (
    blocking: { row: number; publisher?: string; text: string; reason: string }[],
    advisory: { row: number; publisher?: string; text: string; reason: string }[],
) => {
    const escape = (v: string) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = [
        ['Severity', 'Row', 'Publisher', 'Content', 'Problem'].join(','),
        ...blocking.map((i) =>
            ['Blocking', i.row, i.publisher || '', i.text, i.reason].map((v) => escape(String(v))).join(','),
        ),
        ...advisory.map((i) =>
            ['Warning', i.row, i.publisher || '', i.text, i.reason].map((v) => escape(String(v))).join(','),
        ),
    ];
    saveCsvText(rows.join('\n'), 'publisher-onboarding-issues.csv');
};

/**
 * Saves text the client already has as a file. Distinct from downloadCsv below, which fetches
 * a CSV the server generated - these two never share a body.
 */
function saveCsvText(csv: string, filename: string) {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(link.href), 2000);
}

// ============================= lines =============================

export const fetchLines = async (
    publisherId: string,
): Promise<{ active: ITrackedLine[]; removed: ITrackedLine[] }> => {
    const res = await apiClient.get(`${trackedPublishersBaseUrl}/${publisherId}/lines`);
    return res.data;
};

export const createLine = async (publisherId: string, line: Partial<ITrackedLine>) => {
    // Routed through import rather than plain POST so the one code path does dedupe.
    const res = await apiClient.post(`${trackedPublishersBaseUrl}/${publisherId}/lines/import`, {
        rows: [line],
    });
    return res.data as IImportResult;
};

export const updateLine = async (id: string, line: Partial<ITrackedLine>) => {
    const res = await apiClient.patch(`${trackedLinesBaseUrl}/${id}`, line);
    return res.data as ITrackedLine;
};

export const setLineRemoved = async (id: string, isRemoved: boolean) => {
    const res = await apiClient.patch(`${trackedLinesBaseUrl}/${id}/removed`, { isRemoved });
    return res.data as { modified: number };
};

export const importLines = (publisherId: string, rows: Partial<ITrackedLine>[]) =>
    inChunks(
        rows,
        async (batch) => {
            const res = await apiClient.post(`${trackedPublishersBaseUrl}/${publisherId}/lines/import`, {
                rows: batch,
            });
            return res.data as IImportResult;
        },
        mergeImport,
        EMPTY_IMPORT,
    );

export const importLinesFromText = (publisherId: string, text: string) =>
    inChunks(
        (text || '').split(/\r?\n/),
        async (batch) => {
            const res = await apiClient.post(`${trackedPublishersBaseUrl}/${publisherId}/lines/importText`, {
                text: batch.join('\n'),
            });
            return res.data as IImportResult;
        },
        mergeImport,
        EMPTY_IMPORT,
    );

export const importLinesFromCsv = (publisherId: string, rows: string[][]) =>
    inChunks(
        rows,
        async (batch) => {
            const res = await apiClient.post(`${trackedPublishersBaseUrl}/${publisherId}/lines/importCsv`, {
                rows: batch,
            });
            return res.data as IImportResult;
        },
        mergeImport,
        EMPTY_IMPORT,
    );

/**
 * Adds the same lines to many publishers at once, deduping per publisher.
 *
 * The per-publisher dedupe is the point: a line may already exist for one publisher and be new
 * to the rest, so this is safe to re-run as the buyer list grows.
 */
export const importLinesToAll = (rows: Partial<ITrackedLine>[], publisherIds?: string[]) =>
    inChunks<Partial<ITrackedLine>, IImportToAllResult>(
        rows,
        async (batch) => {
            const res = await apiClient.post(`${trackedPublishersBaseUrl}/actions/lines/importAll`, {
                rows: batch,
                publisherIds,
            });
            return res.data as IImportToAllResult;
        },
        (a, b) => {
            // Fold the per-publisher breakdown by id, so batching stays invisible to the caller.
            const byId = new Map(a.perPublisher.map((p) => [p.publisherId, { ...p }]));
            b.perPublisher.forEach((p) => {
                const existing = byId.get(p.publisherId);
                if (existing) {
                    existing.added += p.added;
                    existing.skipped += p.skipped;
                } else {
                    byId.set(p.publisherId, { ...p });
                }
            });
            return {
                added: a.added + b.added,
                skipped: a.skipped + b.skipped,
                perPublisher: Array.from(byId.values()),
            };
        },
        { added: 0, skipped: 0, perPublisher: [] },
    );

export interface IRowIssue {
    /** 1-based, matching the line number in the user's own file or paste. */
    row: number;
    text: string;
    reason: string;
}

export interface IImportPreview {
    rowsRead: number;
    invalid: number;
    wouldAdd: { domain: string; pubId: string; relationship: string; certId?: string }[];
    wouldSkip: number;
    issues: IRowIssue[];
}

/**
 * The message the server actually sent, rather than axios's generic wrapper.
 *
 * Without this, a 409 from the duplicate-domain guard surfaces as "Request failed with status
 * code 409" and the carefully-worded explanation never reaches the user.
 */
export const errorMessage = (err: any): string => {
    const body = err?.response?.data;
    if (typeof body === 'string' && body.trim()) return body;
    if (body?.message) return Array.isArray(body.message) ? body.message.join('; ') : String(body.message);
    return err?.message || String(err);
};

/**
 * Pulls the rejection detail out of a 400 so the UI can list the offending rows.
 *
 * The server refuses an import outright when any row is unusable, and returns the row numbers
 * and reasons in the error body. Without unwrapping it, all the user sees is "Request failed
 * with status code 400".
 */
export const rejectionFromError = (err: any): { message: string; issues: IRowIssue[] } | null => {
    const body = err?.response?.data;
    if (!body || !Array.isArray(body.issues)) return null;
    return { message: body.message || 'Import rejected.', issues: body.issues };
};

/** Dry run: what an upload would do, so a wrongly-shaped file is caught before committing. */
/**
 * Bulk add from raw rows (paste and/or file) to many publishers.
 *
 * `rowsRead`/`invalid` are taken from the first batch's per-file figures rather than summed
 * across publishers, so "understood 3 of 40" means rows in the file - not rows times
 * publishers.
 */
export const importCsvToAll = (
    rows: string[][],
    publisherIds?: string[],
): Promise<IImportToAllResult & { rowsRead?: number; invalid?: number }> =>
    inChunks<string[], IImportToAllResult & { rowsRead?: number; invalid?: number }>(
        rows,
        async (batch) => {
            const res = await apiClient.post(`${trackedPublishersBaseUrl}/actions/lines/importAllCsv`, {
                rows: batch,
                publisherIds,
            });
            return res.data;
        },
        (a, b) => {
            const byId = new Map(a.perPublisher.map((p) => [p.publisherId, { ...p }]));
            b.perPublisher.forEach((p) => {
                const existing = byId.get(p.publisherId);
                if (existing) {
                    existing.added += p.added;
                    existing.skipped += p.skipped;
                } else {
                    byId.set(p.publisherId, { ...p });
                }
            });
            return {
                added: a.added + b.added,
                skipped: a.skipped + b.skipped,
                rowsRead: (a.rowsRead ?? 0) + (b.rowsRead ?? 0),
                invalid: (a.invalid ?? 0) + (b.invalid ?? 0),
                perPublisher: Array.from(byId.values()),
            };
        },
        { added: 0, skipped: 0, rowsRead: 0, invalid: 0, perPublisher: [] },
    );

export const previewLinesCsv = (publisherId: string, rows: string[][]): Promise<IImportPreview> =>
    inChunks<string[], IImportPreview>(
        rows,
        async (batch) => {
            const res = await apiClient.post(`${trackedPublishersBaseUrl}/${publisherId}/lines/previewCsv`, {
                rows: batch,
            });
            return res.data as IImportPreview;
        },
        (a, b) => ({
            rowsRead: a.rowsRead + b.rowsRead,
            invalid: a.invalid + b.invalid,
            wouldAdd: [...a.wouldAdd, ...b.wouldAdd],
            wouldSkip: a.wouldSkip + b.wouldSkip,
            issues: [...a.issues, ...b.issues],
        }),
        { rowsRead: 0, invalid: 0, wouldAdd: [], wouldSkip: 0, issues: [] },
    );

/** Dry run for a paste, mirroring the file preview so both paths warn the same way. */
export const previewLinesText = async (publisherId: string, text: string): Promise<IImportPreview> => {
    const res = await apiClient.post(`${trackedPublishersBaseUrl}/${publisherId}/lines/previewText`, { text });
    return res.data as IImportPreview;
};

/** Dry run for the bulk add. Validation is per-file, so this isn't chunked or fanned out. */
export const previewCsvToAll = async (
    rows: string[][],
): Promise<{ rowsRead: number; invalid: number; understood: number; issues: IRowIssue[] }> => {
    const res = await apiClient.post(`${trackedPublishersBaseUrl}/actions/lines/previewAllCsv`, { rows });
    return res.data;
};

export const previewDeleteCsv = (
    publisherId: string,
    rows: string[][],
): Promise<{ rowsRead: number; invalid: number; lookups: any[] }> =>
    inChunks<string[], { rowsRead: number; invalid: number; lookups: any[] }>(
        rows,
        async (batch) => {
            const res = await apiClient.post(
                `${trackedPublishersBaseUrl}/${publisherId}/lines/previewDeleteCsv`,
                { rows: batch },
            );
            return res.data;
        },
        (a, b) => ({
            rowsRead: a.rowsRead + b.rowsRead,
            invalid: a.invalid + b.invalid,
            lookups: [...a.lookups, ...b.lookups],
        }),
        { rowsRead: 0, invalid: 0, lookups: [] },
    );

/**
 * Dry run for a typed or pasted removal list, mirroring previewDeleteCsv.
 *
 * Not chunked: a paste is bounded by what a person will type, unlike an uploaded file.
 */
export const previewDeleteText = async (
    publisherId: string,
    text: string,
): Promise<{ rowsRead: number; invalid: number; lookups: any[]; issues: IRowIssue[] }> => {
    const res = await apiClient.post(`${trackedPublishersBaseUrl}/${publisherId}/lines/previewDeleteText`, {
        text,
    });
    return res.data;
};

export const massDeleteFromText = async (
    publisherId: string,
    text: string,
): Promise<{ removed: number; notFound: number }> => {
    const res = await apiClient.post(`${trackedPublishersBaseUrl}/${publisherId}/lines/massDeleteText`, { text });
    return res.data;
};

export const massDeleteFromCsv = (publisherId: string, rows: string[][]) =>
    inChunks<string[], { removed: number; notFound: number }>(
        rows,
        async (batch) => {
            const res = await apiClient.post(`${trackedPublishersBaseUrl}/${publisherId}/lines/massDeleteCsv`, {
                rows: batch,
            });
            return res.data;
        },
        (a, b) => ({ removed: a.removed + b.removed, notFound: a.notFound + b.notFound }),
        { removed: 0, notFound: 0 },
    );

export const purgeRemovedLines = async (publisherId: string, lineIds: string[]) => {
    const res = await apiClient.post(`${trackedPublishersBaseUrl}/${publisherId}/lines/purgeRemoved`, { lineIds });
    return res.data as { purged: number };
};

// ============================= checking =============================

export const checkPublisher = async (publisherId: string): Promise<ICheckResult> => {
    const res = await apiClient.post(`${trackedPublishersBaseUrl}/${publisherId}/check`, {});
    return res.data as ICheckResult;
};

/**
 * Analyses a publisher's file supplied by hand.
 *
 * Posts the file as raw text to a route with its own 25mb limit, rather than as JSON under the
 * global 1mb cap - some publishers' app-ads.txt files run to several MB and would otherwise
 * fail with an opaque 413.
 */
export const checkPublisherFromText = async (publisherId: string, content: string): Promise<ICheckResult> => {
    const res = await apiClient.post(`${trackedPublishersBaseUrl}/${publisherId}/checkFromFile`, content, {
        headers: { 'Content-Type': 'text/plain' },
    });
    return res.data as ICheckResult;
};

/**
 * Starts a sweep. Returns as soon as the work is queued, not when it finishes.
 *
 * A full sweep can run for minutes; the platform cuts HTTP requests off at 60s, so waiting for
 * a synchronous response meant a 504 while the work carried on server-side. Poll
 * fetchCheckAllStatus for progress.
 */
export const startCheckAll = async (publisherIds?: string[]): Promise<{ accepted: true; sweepId: string }> => {
    const res = await apiClient.post(`${trackedPublishersBaseUrl}/actions/checkAll`, { publisherIds });
    return res.data;
};

/** Finished sweeps for this seat, newest first — survives reloads and navigation. */
export const fetchCheckAllLog = async (): Promise<ISweepLog[]> => {
    const res = await apiClient.get(`${trackedPublishersBaseUrl}/actions/checkAll/log`);
    return (res.data as ISweepLog[]) || [];
};

/** Progress of this seat's most recent sweep. Null if it has never run one. */
export const fetchCheckAllStatus = async (): Promise<ISweepStatus | null> => {
    const res = await apiClient.get(`${trackedPublishersBaseUrl}/actions/checkAll/status`);
    return (res.data as ISweepStatus) || null;
};

// ============================= update lists =============================

export const fetchUpdateList = async (publisherId: string): Promise<IUpdateList> => {
    const res = await apiClient.get(`${trackedPublishersBaseUrl}/${publisherId}/updateList`);
    return res.data as IUpdateList;
};

/** Downloads a CSV. Mirrors the runLogs CSV pattern: fetch text, Blob, anchor click. */
const downloadCsv = async (url: string, filename: string) => {
    const res = await apiClient.get(url, {
        headers: { Accept: 'text/csv, application/json' },
        responseType: 'text',
    });
    const blob = new Blob([res.data as any], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(link.href), 2000);
};

export const downloadUpdateListCsv = (publisherId: string, publisherName: string) =>
    downloadCsv(
        `${trackedPublishersBaseUrl}/${publisherId}/updateList/csv`,
        `${publisherName || 'publisher'}-update-list.csv`,
    );

export const downloadCombinedUpdateListCsv = () =>
    downloadCsv(`${trackedPublishersBaseUrl}/actions/updateList/csv`, 'update-lists_all-publishers.csv');

// ============================= generating =============================

export const composeLinesForPublisher = async (publisherId: string): Promise<IComposeLinesResult> => {
    const res = await apiClient.get(`${trackedPublishersBaseUrl}/${publisherId}/composeLines`);
    return res.data as IComposeLinesResult;
};

/** Publisher-agnostic generation, preserving the standalone Lines Generator workflow. */
// ============================= all lines =============================

export const fetchDistinctLineSpecs = async (): Promise<IDistinctLineSpec[]> => {
    const res = await apiClient.get(`${trackedLinesBaseUrl}/actions/distinctSpecs`);
    return res.data as IDistinctLineSpec[];
};

/** Every old line across the seat, with its publisher. */
export const fetchOldLines = async (): Promise<IOldLineRow[]> => {
    const res = await apiClient.get(`${trackedLinesBaseUrl}/actions/oldLines`);
    return (res.data as IOldLineRow[]) || [];
};

export const activateLineOnPublishers = async (
    spec: { domain: string; pubId: string; relationship: string; certId?: string },
    publisherIds: string[],
): Promise<IActivateLineResult> => {
    const res = await apiClient.post(`${trackedLinesBaseUrl}/actions/activate`, {
        ...spec,
        publisherIds,
    });
    return res.data as IActivateLineResult;
};

/**
 * Removes one line from chosen publishers - the mirror of activateLineOnPublishers.
 *
 * An empty `publisherIds` would mean "every publisher in the seat" to the server, which is
 * not something this should ever do by accident, so it's refused here rather than sent.
 *
 * The lookup deliberately carries the relationship: in the all-lines view a row *is* a
 * distinct domain+pubId+relationship, so removing DIRECT must not also take out a RESELLER
 * line the user can see listed separately.
 */
export const removeLineFromPublishers = async (
    spec: { domain: string; pubId: string; relationship: string },
    publisherIds: string[],
): Promise<{ removed: number; notFound: number }> => {
    if (!publisherIds.length) {
        throw new Error('Pick at least one publisher to remove this line from.');
    }
    const res = await apiClient.post(`${trackedPublishersBaseUrl}/actions/lines/massDelete`, {
        lookups: [spec],
        publisherIds,
    });
    return res.data as { removed: number; notFound: number };
};
